import { Flex, Form, FormButton, FormTextField, PaperSection } from '@shared/components';
import { useHasPermission, WithPermission } from '@shared/permissions';
import { useSnackbar } from '@shared/snackbar';
import { zodResolver } from '@shared/utils';
import { useUpdateAppRoleMutation } from '@user-management/graphql';
import { APP_PERMISSIONS } from '@user-management/permissions';
import { useRole } from '@user-management/role-management';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

enum EditRoleErrorType {
  DUPLICATE_NAME = 'ALREADY_EXISTS_PLEASE_CHOOSE_A_DIFFERENT_NAME',
}

const ERROR_MESSAGE: Record<EditRoleErrorType, string> = {
  [EditRoleErrorType.DUPLICATE_NAME]: 'This Role name already exists.',
};

const RoleDetailsSchema = z.object({
  name: z.string().min(1, 'Please enter role name.'),
  description: z.string().optional(),
});
type RoleDetailsFormInput = z.infer<typeof RoleDetailsSchema>;

export default function RoleDetails() {
  const { showSnackbar } = useSnackbar();
  const hasUpdateRolePermission = useHasPermission(APP_PERMISSIONS.SETTING.ROLE.UPDATE);

  const { appRoleData, isMyRole } = useRole();

  const formContext = useForm<RoleDetailsFormInput>({
    disabled: isMyRole || !hasUpdateRolePermission,
    defaultValues: {
      name: appRoleData.name,
      description: appRoleData.description,
    },
    resolver: zodResolver(RoleDetailsSchema),
  });
  const { formState, handleSubmit } = formContext;

  const handleUpdateAppRoleError = (errorMessage: string) => {
    let message = errorMessage;
    if (
      EditRoleErrorType.DUPLICATE_NAME.includes(errorMessage) ||
      errorMessage.includes(EditRoleErrorType.DUPLICATE_NAME)
    ) {
      message = ERROR_MESSAGE[EditRoleErrorType.DUPLICATE_NAME];
      formContext.setError('name', { message: message });
      return;
    } else {
      showSnackbar({ message: errorMessage, variant: 'error' });
    }
  };

  const [updateAppRole] = useUpdateAppRoleMutation({
    onCompleted: (data) => {
      if (data?.suUpdateAppRole.success) {
        showSnackbar({ message: 'Role Updated Successfully', variant: 'success' });
        const { appRole } = data.suUpdateAppRole;
        formContext.reset({
          name: appRole?.name ?? '',
          description: appRole?.description ?? '',
        });
      } else {
        handleUpdateAppRoleError(data?.suUpdateAppRole?.message ?? '');
      }
    },
    onError: (error) => {
      handleUpdateAppRoleError(error.message);
    },
  });

  const handleSaveRoleDetails = async (data: RoleDetailsFormInput) => {
    const { name, description } = data;
    await updateAppRole({ variables: { data: { appRoleId: appRoleData.id, name, description } } });
  };

  return (
    <FormProvider {...formContext}>
      <Form onSubmit={handleSubmit(handleSaveRoleDetails)}>
        <PaperSection title='Role Details' sx={{ mb: (theme) => theme.spacing(4) }}>
          <Flex
            sx={(theme) => ({
              flexDirection: 'column',
              gap: theme.spacing(4),
              p: theme.spacing(6),
              '& .MuiInputLabel-root': { pb: theme.spacing(1) },
            })}
          >
            <FormTextField
              name='name'
              label='Role Name'
              disabled={formState.isSubmitting}
              required
            />
            <FormTextField
              name='description'
              label='Role Description'
              placeholder='Enter Role Description'
              multiline
              minRows={2}
              disabled={formState.isSubmitting}
            />
          </Flex>
        </PaperSection>
        <WithPermission requiredPermission={APP_PERMISSIONS.SETTING.ROLE.UPDATE}>
          <FormButton label='Save Changes' />
        </WithPermission>
      </Form>
    </FormProvider>
  );
}
